import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { TableColumn } from '@/components/ui/tables/Table';
import type { SelectorOption } from '@/components/ui';
import { locationAssignedSummaryService } from '@/api/domains/checkInLocation/services/locationAssignedSummary.service';
import { checkInLocationService } from '@/api/domains/checkInLocation/checkInLocation.service';
import { locationEmployeesAssignedService } from '@/api/domains/checkInLocation/services/locationEmployeesAssigned.service';
import { employeeBasicService } from '@/api/domains/employee';
import type { AssignedLocationSummaryItem } from '@/api/domains/checkInLocation';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { useExport } from '@/hooks/ui/useExport';
import type { UIExportFormat } from '@/types/fileExport.types';


const ALL_VALUE = '';

interface AssignedTabFilters {
  locationId?: number;
  employeeId?: number;
}


export interface UseAssignedTabResult {
  rows: AssignedLocationSummaryItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<AssignedLocationSummaryItem>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  isLoading: boolean;
  onDownload: (format: UIExportFormat) => Promise<void>;
  isExporting: boolean;
  locationOptions: SelectorOption[];
  selectedLocationValue: string;
  onLocationChange: (value: string | string[]) => void;
  employeeOptions: SelectorOption[];
  selectedEmployeeValue: string;
  onEmployeeChange: (value: string | string[]) => void;
  onEmployeeSearch: (search: string) => void;
  loadMoreEmployees: (() => Promise<void>) | undefined;
  employeeHasMore: boolean;
  isLoadingEmployees: boolean;
}

export function useAssignedTab(): UseAssignedTabResult {
  const { t: translate } = useTranslation('checkInLocation');
  const { showApiError } = useUI();

  const [baseLocationOptions, setBaseLocationOptions] = useState<SelectorOption[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);

  // ── All-employees mode (lazy: only loads when user types in search) ─────────
  const [allEmployeeOptions, setAllEmployeeOptions] = useState<SelectorOption[]>([]);
  const [isLoadingAllEmployees, setIsLoadingAllEmployees] = useState(false);
  const [allEmployeeHasMore, setAllEmployeeHasMore] = useState(false);
  const allEmployeePageRef = useRef(1);
  const allEmployeeSearchRef = useRef('');

  // ── Location-employees mode (loaded when a location is selected) ───────────
  const [locationEmployeeOptions, setLocationEmployeeOptions] = useState<SelectorOption[]>([]);
  const [isLoadingLocationEmployees, setIsLoadingLocationEmployees] = useState(false);

  const [selectedEmployeeValue, setSelectedEmployeeValue] = useState<string>(ALL_VALUE);

  // ── Main list ──────────────────────────────────────────────────────────────

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<AssignedTabFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<AssignedLocationSummaryItem> | null> => {
      const response = await locationAssignedSummaryService.list(
        {
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          sort: params.sort
            ? { key: params.sort.key, direction: params.sort.direction.toLowerCase() as 'asc' | 'desc' }
            : undefined,
          locationId: params.filters.locationId,
          employeeId: params.filters.employeeId,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code !== 'ABORTED') {
          showApiError(response, translate('assigned.messages.loadError'));
        }
        return null;
      }

      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading: isLoading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
  } = usePaginatedList<AssignedLocationSummaryItem, AssignedTabFilters>({
    fetchFn,
    initialFilters: {},
    initialSort: { key: 'name', direction: 'ASC' },
    orderByMap: {
      id: 'locationId',
      name: 'name',
      assignedCount: 'assignedEmployeesCount',
    },
  });

  const { onExport: onDownload, isExporting } = useExport<UIExportFormat>({
    exportFn: (_format) => locationAssignedSummaryService.download({
      locationId: filters.locationId,
      employeeId: filters.employeeId,
    }),
    errorMessage: translate('assigned.messages.downloadError'),
  });

  // ── Location options (loaded once at mount) ────────────────────────────────

  const loadLocationOptions = useCallback(async () => {
    const response = await checkInLocationService.list({ isActive: true, pageSize: 1000 });
    if (response.success && response.data) {
      setBaseLocationOptions(
        response.data.items.map((location) => ({
          value: String(location.id),
          label: location.name,
        })),
      );
    } else {
      showApiError(response, translate('assigned.messages.loadError'));
    }
  }, [showApiError, translate]);

  useEffect(() => { void loadLocationOptions(); }, [loadLocationOptions]);

  // ── All-employees search (lazy — no load on mount) ─────────────────────────

  const searchAllEmployees = useCallback(async (
    search: string,
    pageNumber: number,
    append: boolean,
  ) => {
    setIsLoadingAllEmployees(true);
    try {
      const response = await employeeBasicService.getBasic({
        page: pageNumber,
        pageSize: 20,
        searchText: search || null,
      });
      if (response.success && response.data) {
        const newOptions = response.data.items.map((employee) => ({
          value: String(employee.id),
          label: employee.rut
            ? `${employee.fullName} (${employee.rut})`
            : employee.fullName,
        }));
        setAllEmployeeOptions((previous) => append ? [...previous, ...newOptions] : newOptions);
        setAllEmployeeHasMore(response.data.hasNextPage);
        allEmployeePageRef.current = pageNumber;
      }
    } finally {
      setIsLoadingAllEmployees(false);
    }
  }, []);

  const onAllEmployeeSearch = useCallback((search: string) => {
    allEmployeeSearchRef.current = search;
    void searchAllEmployees(search, 1, false);
  }, [searchAllEmployees]);

  const loadMoreAllEmployees = useCallback(async () => {
    if (!isLoadingAllEmployees && allEmployeeHasMore) {
      await searchAllEmployees(
        allEmployeeSearchRef.current,
        allEmployeePageRef.current + 1,
        true,
      );
    }
  }, [isLoadingAllEmployees, allEmployeeHasMore, searchAllEmployees]);

  // ── Location-employees load (when a specific location is selected) ──────────

  const loadEmployeesForLocation = useCallback(async (locationId: number) => {
    setIsLoadingLocationEmployees(true);
    const response = await locationEmployeesAssignedService.getAssigned(
      locationId,
      { pageSize: 1000 },
    );
    setIsLoadingLocationEmployees(false);
    if (response.success && response.data) {
      setLocationEmployeeOptions(
        response.data.items.map((employee) => ({
          value: String(employee.employeeId),
          label: employee.employeeName,
        })),
      );
    } else {
      showApiError(response, translate('assigned.messages.loadError'));
    }
  }, [showApiError, translate]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const onLocationChange = useCallback((value: string | string[]) => {
    const rawValue = Array.isArray(value) ? (value[0] ?? ALL_VALUE) : value;
    const locationId = rawValue ? Number(rawValue) : null;

    setSelectedLocationId(locationId);
    setSelectedEmployeeValue(ALL_VALUE);
    setAllEmployeeOptions([]);
    allEmployeeSearchRef.current = '';
    setFilters({ locationId: locationId ?? undefined, employeeId: undefined });

    if (locationId) {
      void loadEmployeesForLocation(locationId);
    } else {
      setLocationEmployeeOptions([]);
    }
  }, [setFilters, loadEmployeesForLocation]);

  const onEmployeeChange = useCallback((value: string | string[]) => {
    const id = Array.isArray(value) ? (value[0] ?? ALL_VALUE) : value;
    setSelectedEmployeeValue(id);
    setFilters((previous) => ({ ...previous, employeeId: id ? Number(id) : undefined }));
  }, [setFilters]);

  // ── Derived values ─────────────────────────────────────────────────────────

  const locationOptions: SelectorOption[] = useMemo(() => [
    { value: ALL_VALUE, label: translate('assigned.filters.allLocations') },
    ...baseLocationOptions,
  ], [baseLocationOptions, translate]);

  const selectedLocationValue = selectedLocationId ? String(selectedLocationId) : ALL_VALUE;

  const isLocationMode = selectedLocationId !== null;

  const employeeOptions: SelectorOption[] = useMemo(() => {
    if (isLocationMode) {
      return [
        { value: ALL_VALUE, label: translate('assigned.filters.allEmployees') },
        ...locationEmployeeOptions,
      ];
    }
    return allEmployeeOptions;
  }, [isLocationMode, locationEmployeeOptions, allEmployeeOptions, translate]);

  const isLoadingEmployees = isLocationMode ? isLoadingLocationEmployees : isLoadingAllEmployees;
  const loadMoreEmployees = isLocationMode ? undefined : loadMoreAllEmployees;
  const employeeHasMore = isLocationMode ? false : allEmployeeHasMore;

  // ── Columns ────────────────────────────────────────────────────────────────

  const columns: TableColumn<AssignedLocationSummaryItem>[] = useMemo(() => [
    {
      key: 'id',
      header: translate('assigned.columns.id'),
      sortable: true,
      render: (row: AssignedLocationSummaryItem) => String(row.id),
    },
    {
      key: 'name',
      header: translate('assigned.columns.name'),
      sortable: true,
    },
    {
      key: 'assignedCount',
      header: translate('assigned.columns.assignedCount'),
      sortable: true,
      render: (row: AssignedLocationSummaryItem) => String(row.assignedCount),
    },
  ], [translate]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    onSearch,
    onSortChange,
    isLoading,
    onDownload,
    isExporting,
    locationOptions,
    selectedLocationValue,
    onLocationChange,
    employeeOptions,
    selectedEmployeeValue,
    onEmployeeChange,
    onEmployeeSearch: onAllEmployeeSearch,
    loadMoreEmployees,
    employeeHasMore,
    isLoadingEmployees,
  };
}
